import { useState } from "react";
import { AlertTriangle, ChevronDown, Clock, Lock, Plus, Sparkles } from "lucide-react";
import type { SkillPackDefinition } from "../../api/types.js";

export type SkillsPageProps = {
  packs: SkillPackDefinition[];
  loading: boolean;
  savingSlug: string | null;
  error: string;
  onToggle: (pack: SkillPackDefinition, enabled: boolean) => void;
};

type SkillsTab = "system" | "personal";

export function SkillsPage({ packs, loading, savingSlug, error, onToggle }: SkillsPageProps) {
  const [tab, setTab] = useState<SkillsTab>("system");
  const isPersonal = tab === "personal";

  return (
    <section className="view skills-view" aria-label="技能">
      <div className="skills-page">
        <header className="skills-head">
          <h1>技能</h1>
          <p>系统提供的研究能力与当前状态。</p>
        </header>

        <div className="skills-tabs" role="tablist" aria-label="技能类型">
          <button
            type="button"
            role="tab"
            aria-selected={!isPersonal}
            className={isPersonal ? "" : "active"}
            onClick={() => setTab("system")}
          >
            系统
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={isPersonal}
            className={isPersonal ? "active" : ""}
            onClick={() => setTab("personal")}
          >
            个人
          </button>
        </div>

        {isPersonal ? (
          <section className="skills-empty" role="tabpanel" aria-label="个人技能">
            <span className="skills-empty-icon" aria-hidden="true"><Plus size={22} /></span>
            <h3>个人技能即将支持</h3>
            <p>你上传或导入的技能会出现在这里。当前仅系统技能可管理。</p>
          </section>
        ) : (
          <section className="skills-list" role="tabpanel" aria-label="系统技能">
            {loading && <p className="skills-note">正在加载技能…</p>}
            {error && <p className="skills-note skills-error">{error}</p>}
            {!loading && !error && packs.length === 0 && <p className="skills-note">暂无可管理技能。</p>}
            {packs.map((pack) => (
              <article className={`skill-card ${pack.enabled ? "" : "is-off"}`} key={pack.slug}>
                <div className="skill-card-icon"><Sparkles size={18} /></div>
                <div className="skill-card-copy">
                  <div className="skill-card-title">
                    <strong>{pack.displayName}</strong>
                    <span className="ver">v{pack.version}</span>
                    {pack.enabled && !pack.installed && (
                      <span className="badge pending"><Clock size={12} />待同步</span>
                    )}
                    {pack.locked && (
                      <span className="badge locked"><Lock size={12} />锁定</span>
                    )}
                    {pack.compatibility !== "native" && (
                      <span className="badge compatibility" title={pack.compatibilityWarnings.join("；")}>
                        <AlertTriangle size={12} />{pack.compatibility === "incompatible" ? "不可用" : "兼容模式"}
                      </span>
                    )}
                  </div>
                  <p className="skill-card-desc">{pack.description}</p>
                  <div className="skill-card-meta">
                    <span>{pack.memberCount} 个技能</span>
                    <span>{pack.origin}</span>
                    <span>{pack.license}</span>
                  </div>
                  <details className="skill-pack-members">
                    <summary><ChevronDown size={14} />查看成员</summary>
                    <div className="skill-pack-member-list">
                      {pack.members.map((member) => (
                        <div className="skill-pack-member" key={member.name}>
                          <strong>{member.displayName}</strong>
                          <code>/{member.name}</code>
                          <p>{member.description}</p>
                        </div>
                      ))}
                    </div>
                  </details>
                </div>
                <label className="skill-toggle">
                  <input
                    type="checkbox"
                    checked={pack.enabled}
                    disabled={pack.locked || savingSlug === pack.slug}
                    onChange={(event) => onToggle(pack, event.currentTarget.checked)}
                    aria-label={`${pack.enabled ? "关闭" : "开启"} ${pack.displayName}`}
                  />
                  <span />
                </label>
              </article>
            ))}
          </section>
        )}
      </div>
    </section>
  );
}
